import type {
  DataGridDataRowTemplateProps,
  DataGridRef,
  DataGridSettingsMenuItem,
  JsonData,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSender,
  RowTemplateType,
} from '@myosh/odin-components';
import { DataGrid, DataSearchLocation, type DynamicFormFieldType } from '@myosh/odin-components';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { LinkedUser } from '../../../../@types/users';
import useFetchUsers from '../../../../hooks/use-fetch-users';
import { useHierarchyTypesQuery } from '../../../../redux/services/hierarchy';
import { setDataGridReference } from '../../../../services/data-grid.service';
import { dateFormatBasedOnFilter } from '../../../../utils/data-transformation-utils';
import { processFieldFilters } from '../../../../utils/filter-url-builder-utils';
import { extractUserIds } from '../../../../utils/linked-user-utils';
import { forceAssert } from '../../../../utils/type-guards';
import { UserFields } from '../../../../utils/user-config';
import { getUsersFieldOptionsData } from '../../../../utils/user-functions';
import { linkedUsersCustomComponents } from '../user-record-link.component';
import { UserRecordLinkFields, usersDataGridColumns } from './user-recordlink-columns-config';

interface SelectedUsersGridProps {
  linkedUsers: Array<LinkedUser>;
  onRemoved: (value: LinkedUser) => void;
  menuGridSettings?: DataGridSettingsMenuItem[];
}

function SelectedUsersGrid({ linkedUsers, onRemoved, menuGridSettings }: SelectedUsersGridProps) {
  const [linkedUsersIds, setLinkedUsersIds] = useState<Array<number>>([]);
  const [gridOptions, setGridOptions] = useState<OdinDataRetrievalOptions>();
  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const gridSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);

  const { users } = useFetchUsers(
    linkedUsersIds,
    UserRecordLinkFields.join(),
    processFieldFilters(gridOptions?.fieldFilters, dateFormatBasedOnFilter),
    gridOptions?.sortedFields
  );
  const { data: hierarchyTypes } = useHierarchyTypesQuery({ archived: 'false' });

  useEffect(() => {
    const userIds = extractUserIds(linkedUsers);
    setLinkedUsersIds(userIds);
  }, [linkedUsers, gridOptions]);

  const gridSettings = useMemo(() => {
    if (hierarchyTypes) {
      return {
        columns: usersDataGridColumns(hierarchyTypes),
        components: linkedUsersCustomComponents,
        fullHeight: true,
        filterLocation: DataSearchLocation.Api,
      };
    }
  }, [hierarchyTypes]);

  const removeUser = (value: LinkedUser) => {
    onRemoved(value);
    gridRef?.current?.api?.data?.removeRowById(value.value);
  };

  const rowTemplate: RowTemplateType = (props: DataGridDataRowTemplateProps, children: ReactNode | ReactNode[]) => (
    <div
      className="flex"
      onClick={() =>
        removeUser({
          value: props.data[UserFields.id] as number,

          text: props.data[UserFields.lastName] + ' ' + props.data[UserFields.firstName],
        })
      }
    >
      {children}
    </div>
  );
  useEffect(() => {
    if (users) {
      gridSubscriber.current?.sendData({ data: forceAssert<JsonData>(users), requestId: gridOptions?.requestId });
    } else {
      gridSubscriber.current?.sendData();
    }
  }, [users]);

  const gridData = useMemo<OdinDataRetrieval>(() => {
    return {
      getSubscriber: (subscriber, fieldType?: DynamicFormFieldType) => {
        if (fieldType !== 'COMBOBOX') {
          gridSubscriber.current = subscriber;
        }
      },
      getData: (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.fieldType === 'COMBOBOX') {
          getUsersFieldOptionsData(subscriber, options);
        } else {
          setGridOptions(options);
        }
      },
    };
  }, []);

  const onDataGridRefCreated = (dataGridRef: DataGridRef) => {
    gridRef.current = dataGridRef;
    setDataGridReference(dataGridRef);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      {gridSettings && (
        <DataGrid
          ref={(gridRef) => {
            if (gridRef) onDataGridRefCreated(gridRef);
          }}
          data={gridData}
          gridSettings={gridSettings}
          rowTemplate={rowTemplate}
          customSettingsMenuItems={menuGridSettings}
          showGridMenu={false}
        />
      )}
    </div>
  );
}

export default SelectedUsersGrid;
